import * as THREE from 'three';
import CameraControls from 'camera-controls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { setupRenderer } from '../scene/setupRenderer.js';
import { LIGHTING } from '../config.js';
import { loadEnvironment } from '../loaders/loadEnvironment.js';
import { loadRolePbr, type RoleDef, type HdrPreset } from '../materials/roleDefs.js';
import { applyRoleOverrides, type RoleGroupsJson } from '../materials/applyRoleOverrides.js';
import { applyTireAO } from '../materials/applyTireAO.js';
import { tuneRav4Materials } from '../materials/rav4Tuning.js';
import { stripMayaNamespace } from '../utils/mayaNamespace.js';
import { createCameraLimits } from '../scene/cameraLimits.js';
import { finalizeFirstPaint } from '../scene/firstPaint.js';
import { type ViewPreset } from '../scene/viewPresets.js';
import { type GlbSource } from '../ui/materialCategoryRules.js';
import { createEventHub, type ViewerContext } from './context.js';
import type { ViewerConfig } from './config.js';
import type { ViewerTool } from './tools/tool.js';
import { TOOLS } from './tools/index.js';
import { initLoadDiag, diagMark, diagInfo } from '../ui/loadDiag.js';

CameraControls.install({ THREE });

const $ = (id: string) => document.getElementById(id);

/**
 * 차종 뷰어 1대를 띄운다. 코어(렌더러/씬/카메라/env/에셋/렌더루프)를 만들고
 * ViewerContext 를 조립한 뒤 등록된 도구를 순회한다.
 * @param tools  등록된 ViewerTool 목록 (기본 TOOLS 레지스트리). 빈 배열이면 도구 없이 뷰만.
 */
export async function createViewer(
  config: ViewerConfig,
  hdriMap: Record<string, string>,
  tools: ViewerTool[] = TOOLS,
): Promise<void> {
  const params = new URLSearchParams(location.search);
  initLoadDiag(params);
  diagMark('boot');
  const rolesOn = (params.get('roles') ?? '1') !== '0';

  // 모바일 가드 (Lexus/800B 와 동일 목적) — ?mobile=0|1 강제, 기본은 coarse pointer / UA 자동 감지.
  const mobileOverride = params.get('mobile');
  const isMobile = mobileOverride === '1' ? true
    : mobileOverride === '0' ? false
    : window.matchMedia('(pointer: coarse)').matches || /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // prod 빌드에서 dev 메트릭 HUD 숨김 (?hud=1 로 강제 표시). Lexus(test-interior) 와 동일.
  if (import.meta.env.PROD && params.get('hud') !== '1') {
    const hudEl = document.getElementById('hud');
    if (hudEl) hudEl.style.display = 'none';
  }

  const renderer = setupRenderer();
  // 모바일: transmission 렌더타깃 1/2 해상도(메모리 1/4) → iOS Safari 메모리 캡 회피.
  renderer.transmissionResolutionScale = isMobile ? 0.5 : 1;
  const appEl = document.getElementById('app')!;
  const camera = new THREE.PerspectiveCamera(35, appEl.clientWidth / appEl.clientHeight, 0.05, 100);
  const scene = new THREE.Scene();

  const controls = new CameraControls(camera, renderer.domElement);
  controls.dampingFactor = 0.06;
  // 모바일 드래그 무게감: 회전 속도 낮추고 드래그 중 댐핑 강화 — 짧은 스와이프에 휙휙 돌지 않게. 데스크톱은 기본값 유지.
  if (isMobile) {
    controls.azimuthRotateSpeed = 0.6;
    controls.polarRotateSpeed = 0.6;
    controls.draggingDampingFactor = 0.15;
  }
  controls.minDistance = 0.1;
  controls.maxDistance = 50;
  const cameraLimits = createCameraLimits(controls);

  // dev 콘솔에서 현재 카메라 포즈 뽑기 (프리셋 값 채집용)
  if (import.meta.env.DEV) {
    (window as Window & { __cam__?: () => unknown }).__cam__ = () => {
      const p = controls.getPosition(new THREE.Vector3());
      const t = controls.getTarget(new THREE.Vector3());
      const r = (v: number) => Math.round(v * 1000) / 1000;
      const pose = { position: [r(p.x), r(p.y), r(p.z)], target: [r(t.x), r(t.y), r(t.z)] };
      console.log('[cam]', JSON.stringify(pose));
      return pose;
    };
  }

  let needsRender = true;
  const invalidate = (): void => { needsRender = true; };

  // role-pbr SSOT
  let roleDefs: Record<string, RoleDef> = {};
  let savedHdr: HdrPreset | null = null;
  let savedPaintOverrides: Record<string, Record<string, Record<string, unknown>>> = {};
  let savedCurrentPaint: string | null = null;
  if (rolesOn) {
    try {
      const loaded = await loadRolePbr(import.meta.env.BASE_URL + config.rolePbr);
      roleDefs = loaded.roles;
      savedHdr = loaded.hdr;
      savedPaintOverrides = loaded.paintOverrides;
      savedCurrentPaint = loaded.currentPaint;
    } catch (e) {
      console.error(`[viewer:${config.id}] loadRolePbr failed`, e);
    }
  }

  const hemiLight = new THREE.HemisphereLight(LIGHTING.hemi.sky, LIGHTING.hemi.ground, LIGHTING.hemi.intensity);
  scene.add(hemiLight);

  // HDRI
  // 모바일이면 저용량 HDRI(config.hdriMobile) 강제 — 5K EXR PMREM 이 모바일 OOM 주범.
  const hdriKey = params.get('hdri')
    ?? (isMobile && config.hdriMobile ? config.hdriMobile : (savedHdr?.preset ?? config.hdriDefault));
  const hdriPath = hdriMap[hdriKey] ?? hdriMap[config.hdriDefault]!;
  const envMap = await loadEnvironment(scene, renderer, hdriPath);
  diagMark('env-loaded');
  let currentEnvMap: THREE.Texture | null = envMap;
  renderer.toneMappingExposure = parseFloat(params.get('exp') ?? '') || savedHdr?.exposure || 1.33;
  scene.environmentIntensity = parseFloat(params.get('env') ?? '') || savedHdr?.intensity || 2.2;
  const bgOn = params.get('bg') !== null ? params.get('bg') === '1' : (savedHdr?.background ?? false);
  if (bgOn) scene.background = envMap;
  if (savedHdr?.rotationY) {
    const r = THREE.MathUtils.degToRad(savedHdr.rotationY);
    scene.environmentRotation.y = r;
    scene.backgroundRotation.y = r;
  }

  const shadowTexPromise = new THREE.TextureLoader()
    .loadAsync(config.shadowTexture)
    .then((tex) => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
      return tex;
    });

  const draco = new DRACOLoader();
  draco.setDecoderPath(import.meta.env.BASE_URL + 'draco/gltf/');
  const ktx2 = new KTX2Loader();
  ktx2.setTranscoderPath(import.meta.env.BASE_URL + 'basis/');
  ktx2.detectSupport(renderer);
  const gltfLoader = new GLTFLoader();
  gltfLoader.setDRACOLoader(draco);
  gltfLoader.setKTX2Loader(ktx2);

  const root = new THREE.Group();
  root.name = config.rootName;
  scene.add(root);

  // role-groups JSON 을 한 번만 받아 차체·액세서리 양쪽에 재사용.
  let groupsJson: RoleGroupsJson | null = null;
  if (rolesOn && config.groups) {
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}${config.groups}?t=${Date.now()}`);
      if (res.ok) groupsJson = (await res.json()) as RoleGroupsJson;
      else console.warn(`[viewer:${config.id}] groups JSON 404 — raw GLB`);
    } catch (e) {
      console.error(`[viewer:${config.id}] groups JSON fetch failed`, e);
    }
  }

  // 에셋 로드 (config.assets 순회). RAV4 는 'O' 1장.
  const roots = new Map<GlbSource, THREE.Object3D>();
  let primary: THREE.Object3D | null = null;
  // baked/deploy GLB(aggressive) 판정 — 파일 재질에 `+++`(Phase1 role 베이크) 가 있으면 true.
  // 반드시 applyRoleOverrides(runtime deriveFor 가 `+++` 생성) 전에 검사. tuner 의 overrides[] 저작 차단용.
  let bakedGlb = false;
  for (const asset of config.assets) {
    if (asset.include && !asset.include(params)) continue;
    const glbPath = typeof asset.glb === 'function' ? asset.glb(params, isMobile) : asset.glb;
    const url = import.meta.env.DEV ? `${glbPath}?t=${Date.now()}` : glbPath;
    diagMark(`glb-fetch-start:${asset.category}`);
    const gltf = await gltfLoader.loadAsync(url);
    diagMark(`glb-parsed:${asset.category}`);
    const group = gltf.scene;
    group.name = `cat_${asset.category}`;
    group.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      for (const m of mats) {
        if (!m) continue;
        if (m.name.includes('+++')) bakedGlb = true; // 파일에 박힌 +++ = 베이크 산출물
        m.name = stripMayaNamespace(m.name);
      }
    });
    if (groupsJson) {
      try {
        const { upgraded, perRole, unmatched } = applyRoleOverrides(group, groupsJson, roleDefs);
        console.log(`[viewer:${config.id}] role override: ${upgraded} mats`, perRole);
        if (unmatched.length) console.warn(`[viewer:${config.id}] unmatched (${unmatched.length})`, unmatched.slice(0, 30));
        if (params.get('nametune') === '1') {
          const bodyHex = parseInt((params.get('body') ?? 'dedede'), 16);
          const { recolored } = tuneRav4Materials(group, { bodyColor: bodyHex });
          console.log(`[viewer:${config.id}] name-based recolor: ${recolored} mats (body=#${bodyHex.toString(16)})`);
        }
      } catch (e) {
        console.error(`[viewer:${config.id}] applyRoleOverrides failed`, e);
      }
    }
    root.add(group);
    roots.set(asset.category, group);
    primary ??= group;
  }
  diagMark('scene-added');

  // 액세서리 — 맥스에서 rav4.glb 공간에 정합한 GLB. 차체와 같은 groups JSON 으로
  // 저장된 mesh 오버라이드(silveraccessery 등)를 재적용. 액세서리 전용 머티리얼은
  // baseline 에 없어 unmatched 로 나오므로 개수만 짧게 로그(warn 스팸 방지).
  if (config.accessories) {
    try {
      const accPath = typeof config.accessories === 'function' ? config.accessories(params, isMobile) : config.accessories;
      const accUrl = import.meta.env.DEV ? `${accPath}?t=${Date.now()}` : accPath;
      const accGltf = await gltfLoader.loadAsync(accUrl);
      accGltf.scene.name = 'accessories';
      accGltf.scene.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh)) return;
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const m of mats) if (m) m.name = stripMayaNamespace(m.name);
      });
      if (groupsJson) {
        const { upgraded, perRole, unmatched } = applyRoleOverrides(accGltf.scene, groupsJson, roleDefs);
        console.log(`[viewer:${config.id}] accessories role override: ${upgraded} mats`, perRole, `(unmatched ${unmatched.length})`);
      }
      root.add(accGltf.scene);
      console.log(`[viewer:${config.id}] accessories loaded`);
      diagMark('accessories-loaded');
    } catch (e) {
      console.error(`[viewer:${config.id}] accessories load failed`, e);
    }
  }

  const primaryRoot = roots.get('O') ?? primary!;

  // 타이어 AO
  if (config.tireAO) {
    try {
      const aoTexture = typeof config.tireAO.aoTexture === 'function'
        ? config.tireAO.aoTexture(params, isMobile)
        : config.tireAO.aoTexture;
      const { hidden, placed } = await applyTireAO(primaryRoot, primaryRoot, gltfLoader, {
        tireMeshNames: config.tireAO.tireMeshNames,
        bakedGlb: config.tireAO.bakedGlb,
        aoTexture,
        tireProps: roleDefs.tire?.props as Record<string, unknown> | undefined,
      });
      console.log(`[viewer:${config.id}] tire AO: hidden=${hidden} placed=${placed}`);
    } catch (e) {
      console.error(`[viewer:${config.id}] tire AO failed`, e);
    }
  }

  // 통계 HUD
  let meshes = 0, tris = 0;
  const matSet = new Set<THREE.Material>();
  primaryRoot.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    meshes++;
    const g = obj.geometry;
    tris += g.index ? g.index.count / 3 : (g.attributes.position?.count ?? 0) / 3;
    const arr = Array.isArray(obj.material) ? obj.material : [obj.material];
    for (const m of arr) if (m) matSet.add(m);
  });
  if ($('meshes')) $('meshes')!.textContent = meshes.toLocaleString();
  if ($('mats')) $('mats')!.textContent = matSet.size.toLocaleString();
  if ($('tris')) $('tris')!.textContent = Math.round(tris).toLocaleString();

  // 마무리: studio 배경 + 바닥 그림자 + 프레이밍 + splash
  const view = (params.get('view') ?? 'full') as ViewPreset;
  const fp = await finalizeFirstPaint({
    scene, root, gltfLoader,
    rolesEnabled: rolesOn, roleDefs,
    shadowTexPromise,
    view, camera, controls, cameraLimits,
    limitsInitial: params.get('limits') === '1',
  });
  if (fp.studioRoot) roots.set('X', fp.studioRoot);

  // ctx 조립
  const ctx: ViewerContext = {
    scene, camera, renderer, controls, cameraLimits, invalidate, gltfLoader,
    roots, root: primaryRoot, roleDefs, rolesOn, hemiLight, hdriMap,
    env: {
      getEnvMap: () => currentEnvMap,
      setHdr: async (keyOrPath, forceType) => {
        const p = hdriMap[keyOrPath] ?? keyOrPath;
        if (!p) return;
        currentEnvMap = await loadEnvironment(scene, renderer, p, forceType);
        invalidate();
      },
      hdriKeys: Object.keys(hdriMap),
      currentPreset: hdriKey,
    },
    config, bakedGlb, params, shadowTexPromise,
    events: createEventHub(),
    saved: { hdr: savedHdr, paintOverrides: savedPaintOverrides, currentPaint: savedCurrentPaint },
  };

  // 도구 루프 — 등록된 도구 중 enabled 만 setup. 한 도구 실패해도 나머지 진행.
  for (const tool of tools) {
    if (!tool.enabled(ctx)) continue;
    try { await tool.setup(ctx); }
    catch (e) { console.error(`[viewer:${config.id}] tool "${tool.id}" 실패`, e); }
  }

  // 렌더 루프
  const clock = new THREE.Clock();
  let frames = 0, acc = 0, hudLast = performance.now(), last = performance.now();
  // diag: HUD 의 frames 는 500ms 마다 리셋되므로 누적 프레임은 별도 카운터로 추적.
  // render-on-demand 라 정지 씬은 프레임이 안 나오므로, diag 활성 시에만 milestone
  // 도달까지 강제 재렌더 (프로덕션 경로는 diagOn=false 라 무영향).
  const DIAG_SURVIVE_FRAMES = 60;
  const diagOn = params.get('diag') === '1';
  let diagFrames = 0;
  function tick(): void {
    const dt = clock.getDelta();
    if (controls.update(dt)) needsRender = true;
    if (diagOn && diagFrames < DIAG_SURVIVE_FRAMES) needsRender = true;
    if (needsRender) {
      renderer.render(scene, camera);
      needsRender = false;
      diagFrames++;
      if (diagFrames === 1) { diagMark('first-frame'); diagInfo(renderer); }
      else if (diagFrames === DIAG_SURVIVE_FRAMES) diagMark(`frames-${DIAG_SURVIVE_FRAMES}`);
      const now = performance.now();
      acc += now - last; last = now; frames++;
      if (now - hudLast > 500) {
        if ($('fps')) $('fps')!.textContent = ((frames * 1000) / acc).toFixed(1);
        if ($('calls')) $('calls')!.textContent = renderer.info.render.calls.toLocaleString();
        frames = 0; acc = 0; hudLast = now;
      }
    } else { last = performance.now(); }
    requestAnimationFrame(tick);
  }
  tick();

  new ResizeObserver(() => {
    const w = appEl.clientWidth, h = appEl.clientHeight;
    if (!w || !h) return;
    camera.aspect = w / h; camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    needsRender = true;
  }).observe(appEl);
}
